import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Input } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { useClient } from '@/stores/useClient.ts'
import { useProfile } from '@/stores/useAuth.ts'
import { HeyThemeConstantProvider, useHeyTheme } from '@/contexts/heyTheme.tsx'
import { PORTAL_PRIMARY, PORTAL_NEUTRAL, PORTAL_TEXT, PORTAL_BG, PORTAL_BORDER } from '@/controller/driveDefaults.ts'
import usePicFieldTitle from '@/hooks/usePicFieldTitle.ts'

const TOTAL_STEPS = 2

export default function PortalOnboardingPage() {
  usePicFieldTitle()

  return (
    <HeyThemeConstantProvider colorPrimary={PORTAL_PRIMARY} colorNeutral={PORTAL_NEUTRAL} colorContent={PORTAL_TEXT} font="Cormorant Infant">
      <OnboardingContent />
    </HeyThemeConstantProvider>
  )
}

function OnboardingContent() {
  const { style: themeStyle } = useHeyTheme()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { updatePortalMine, getPortalMine } = useClient()
  const { profile, setProfile, authenticationToken } = useProfile()
  const { updateProfile } = useClient()

  // Load profile on mount (standalone page, no Portal wrapper)
  const { data: portalData } = useQuery({
    queryKey: ['portal', 'mine'],
    queryFn: () => getPortalMine(),
    enabled: authenticationToken != null,
  })

  useEffect(() => {
    if (portalData?.data) {
      setProfile(portalData.data.profile)
    }
  }, [portalData, setProfile])

  const [step, setStep] = useState(0)
  const [fadeState, setFadeState] = useState<'in' | 'out'>('in')
  const [isLoading, setIsLoading] = useState(false)

  // Step 1: Name
  const [displayName, setDisplayName] = useState('')

  // Populate display name from billing_first_name (or fallback to profile display_name)
  useEffect(() => {
    if (!displayName && portalData?.data) {
      const account = portalData.data.account as any
      const name = account?.billing_first_name || portalData.data.profile?.display_name || ''
      if (name) setDisplayName(name)
    }
  }, [portalData])

  function transitionTo(nextStep: number) {
    setFadeState('out')
    setTimeout(() => {
      setStep(nextStep)
      setFadeState('in')
    }, 300)
  }

  async function handleNameNext() {
    if (!displayName.trim()) return
    if (!profile) return
    setIsLoading(true)
    try {
      // Save name
      await Promise.all([
        updateProfile(profile.username, { display_name: displayName.trim() }),
        updatePortalMine({ billing_first_name: displayName.trim() }),
      ])
      setProfile({ ...profile, display_name: displayName.trim() })
      queryClient.invalidateQueries({ queryKey: ['portal', 'mine'] })
      transitionTo(1)
    } catch (e) {
      console.error('Error saving onboarding data:', e)
    } finally {
      setIsLoading(false)
    }
  }

  function handleStart() {
    setFadeState('out')
    setTimeout(() => {
      navigate('/portal/dashboard')
    }, 300)
  }

  return (
    <>
      <style>{themeStyle}{`:root { --portal-bg: ${PORTAL_BG}; --portal-border: ${PORTAL_BORDER}; }`}</style>
      <div className="min-h-screen bg-[var(--portal-bg)] flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          {/* Step indicator */}
          <div className="flex justify-center gap-2 mb-8">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <div
                key={i}
                className={`h-1 rounded-full transition-all duration-300 ${
                  i === step ? 'w-8 bg-primary' : i < step ? 'w-8 bg-primary opacity-40' : 'w-8 bg-secondary'
                }`}
              />
            ))}
          </div>

          {/* Step content with fade */}
          <div
            className={`transition-opacity duration-300 ${fadeState === 'in' ? 'opacity-100' : 'opacity-0'}`}
          >
            {step === 0 && (
              <StepWelcome
                displayName={displayName}
                setDisplayName={setDisplayName}
                onNext={handleNameNext}
                isLoading={isLoading}
              />
            )}
            {step === 1 && (
              <StepDone onStart={handleStart} />
            )}
          </div>
        </div>
      </div>
    </>
  )
}

// --- Step 1: Welcome ---

function StepWelcome({
  displayName, setDisplayName, onNext, isLoading,
}: {
  displayName: string
  setDisplayName: (v: string) => void
  onNext: () => void
  isLoading: boolean
}) {
  return (
    <div className="bg-neutral border border-[var(--portal-border)] p-8 md:p-12">
      <h1 className="text-3xl md:text-4xl font-serif font-light mb-2">Hallo!</h1>
      <p className="opacity-70 mb-8">Schön, dass du dabei bist. Wie heißt du?</p>

      <form onSubmit={e => { e.preventDefault(); onNext() }} className="flex flex-col gap-6">
        <label className="form-control w-full">
          <div className="label">
            <span className="label-text">Vorname</span>
          </div>
          <Input
            placeholder="Dein Vorname"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            autoFocus
            required
          />
          <div className="label">
            <span className="label-text-alt opacity-50">
              Wird deinen Kunden und Gästen angezeigt, damit sie wissen, wer die Fotos geschossen hat. Du kannst das jederzeit in den Einstellungen ändern.
            </span>
          </div>
        </label>

        <div className="flex justify-end">
          <Button type="submit" color="accent" loading={isLoading} disabled={isLoading || !displayName.trim()}>
            Weiter
          </Button>
        </div>
      </form>
    </div>
  )
}

// --- Step 2: Done ---

function StepDone({ onStart }: { onStart: () => void }) {
  return (
    <div className="bg-neutral border border-[var(--portal-border)] p-8 md:p-12 text-center">
      <h1 className="text-3xl md:text-4xl font-serif font-light mb-2">Perfekt!</h1>
      <p className="opacity-70 mb-8">
        Alles eingerichtet. Du kannst jetzt dein erstes Projekt erstellen und loslegen.
      </p>

      <Button color="accent" onClick={onStart}>
        Jetzt starten
      </Button>
    </div>
  )
}
